import * as React from 'react'
import { getId } from '@basehub/utils/id/index'

import type { Block, MediaBlockValueType } from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { uploadFileToRepo } from '~/lib/file-utils'
import { getFileHashSHA256 } from '@basehub/data/blob/file-utils'

export const useEditorDropAssets = ({
  block,
  parentBlock,
  path,
}: {
  block: Block
  parentBlock: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const teamId = data?.team.id
  const repoId = data?.repoId
  const replicache = data?.replicache
  const [isDragging, setIsDragging] = React.useState(false)

  const onDrop = React.useCallback(
    async (
      e: React.DragEvent<HTMLDivElement>,
      position: 'after' | 'before'
    ) => {
      if (!replicache || !path) return
      if (!teamId || !repoId) {
        throw new Error(
          'Could not find team or repo information, this is a bug'
        )
      }

      if (
        !e.dataTransfer ||
        !e.dataTransfer.files ||
        !e.dataTransfer.files[0]
      ) {
        return
      }

      e.preventDefault()

      const validFiles = Array.from(e.dataTransfer.files).reduce<
        {
          file: File
          type: MediaBlockValueType
        }[]
      >((acum, current) => {
        if (!current.type) return acum
        let type
        if (
          current.type.includes('video/') ||
          current.type.includes('image/') ||
          current.type.includes('audio/')
        ) {
          type = current.type.split('/')[0]
        } else type = 'file'

        acum.push({
          type: type as MediaBlockValueType,
          file: current,
        })

        return acum
      }, [])

      if (!validFiles.length) return

      const promises = validFiles.map(async ({ type, file }) => {
        const id = getId()
        const title = file.name.replace(/\.[^/.]+$/, '')

        const { newBlock } = await replicache.mutate.createBlock({
          data: {
            id,
            title,
            type: 'media',
            value: { status: 'uploading' },
          },
          path,
          position: {
            type: position,
            targetId: block.id,
          },
          parentInstanceChildTarget: parentBlock.instanceChildTarget,
          isoNow: new Date().toISOString(),
          idSeed: getId(),
        })
        const { uploadURL } = await uploadFileToRepo(file, {
          teamId,
          repoId,
          fileHash: await getFileHashSHA256(file),
        })

        if (type === 'image') {
          const image = new Image()
          image.onload = async () => {
            await replicache.mutate.updateBlock({
              id: newBlock.id,
              instanceChildTarget: newBlock.instanceChildTarget,
              data: {
                type: 'media',
                value: {
                  type,
                  status: 'uploaded',
                  mimeType: file.type,
                  fileName: file.name,
                  fileSize: file.size,
                  url: uploadURL,
                  width: image.width,
                  height: image.height,
                  cloudflareImage: {
                    uploadState: 'will-not-upload',
                    url: null,
                    reason: 'deprecated',
                  },
                  altText: null,
                  lastModified: file.lastModified,
                },
              },
              path,
              strategy: 'merge',
              isoNow: new Date().toISOString(),
            })
          }
          image.src = URL.createObjectURL(file)
        } else if (type === 'video') {
          const videoElement = document.createElement('video')
          videoElement.addEventListener('loadedmetadata', async () => {
            await replicache.mutate.updateBlock({
              id: newBlock.id,
              instanceChildTarget: newBlock.instanceChildTarget,
              data: {
                type: 'media',
                value: {
                  type,
                  status: 'uploaded',
                  mimeType: file.type,
                  fileName: file.name,
                  fileSize: file.size,
                  width: videoElement.videoWidth,
                  height: videoElement.videoHeight,
                  url: uploadURL,
                  lastModified: file.lastModified,
                },
              },
              path,
              strategy: 'merge',
              isoNow: new Date().toISOString(),
            })
            videoElement.remove()
          })
          const src = URL.createObjectURL(file)
          videoElement.src = src
        } else {
          await replicache.mutate.updateBlock({
            id: newBlock.id,
            instanceChildTarget: newBlock.instanceChildTarget,
            data: {
              type: 'media',
              value: {
                type,
                status: 'uploaded',
                mimeType: file.type,
                fileName: file.name,
                fileSize: file.size,
                url: uploadURL,
                lastModified: file.lastModified,
              },
            },
            path,
            strategy: 'merge',
            isoNow: new Date().toISOString(),
          })
        }
      })

      await Promise.all(promises)
    },
    [
      path,
      replicache,
      repoId,
      teamId,
      block.id,
      parentBlock.instanceChildTarget,
    ]
  )

  return { isDragging, setIsDragging, onDrop }
}
